import { Router, RouterLink, RouterOutlet } from '@angular/router';
import { NgIf, NgClass, AsyncPipe, UpperCasePipe } from '@angular/common';
import { Component, inject, OnDestroy, OnInit, ViewChild } from '@angular/core';

import { combineLatest, Subscription } from 'rxjs';
import { Menu } from 'primeng/menu';
import { MenuItem } from 'primeng/api';
import { TimeAgoPipe } from 'ngx-moment';
import { MenuModule } from 'primeng/menu';
import { ToastrService } from 'ngx-toastr';
import { TooltipModule } from 'primeng/tooltip';
import { filter, shareReplay, startWith, tap } from 'rxjs/operators';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { TranslateService, TranslatePipe } from '@ngx-translate/core';

import { Hub } from '@qwyk/models';
import { environment } from '@qwyk/hub/environment';
import { MagayaConnectionService } from '@qwyk/core';
// eslint-disable-next-line @nx/enforce-module-boundaries
import { MD5Pipe, UrlEncodePipe } from '@qwyk/ui-standalone';
// eslint-disable-next-line @nx/enforce-module-boundaries
import { AuthenticationFacade } from '@qwyk/hub/authentication';

import {
    slideDown,
    fadeInOutAnimation,
    fadeInDelayAnimation,
} from '../../helpers/reusableAnimations';
import { versions } from '../../../environments/versions';
import { SideBarComponent } from '../side-bar/side-bar.component';
import { MenuSideBarService } from '../../services/menu-side-bar.service';

@Component({
    selector: 'app-index-new',
    templateUrl: './index-new.component.html',
    styleUrls: ['./index-new.component.scss'],
    animations: [fadeInOutAnimation, fadeInDelayAnimation, slideDown],
    standalone: true,
    imports: [
        NgIf,
        NgClass,
        MD5Pipe,
        AsyncPipe,
        MenuModule,
        RouterLink,
        RouterOutlet,
        TooltipModule,
        UpperCasePipe,
        UrlEncodePipe,
        TranslatePipe,
        SideBarComponent,
        FontAwesomeModule
    ],
})
export class IndexNewComponent implements OnInit, OnDestroy {
    private router = inject(Router);
    private toastr = inject(ToastrService);
    private timeagoPipe = inject(TimeAgoPipe);
    private translate = inject(TranslateService);
    public menuSideBar = inject(MenuSideBarService);
    private authentication: AuthenticationFacade = inject(AuthenticationFacade);
    private magayaConnectionService: MagayaConnectionService = inject(
        MagayaConnectionService
    );

    public year = new Date().getFullYear();
    public versionInfo = versions;
    public env = environment;
    public magayaConnectionStatus$ = this.magayaConnectionService
        .getMagayaConnectionStatus()
        .pipe(
            tap(status => this.bannerFactory(status)),
            filter(
                (status: Hub.MagayaConnectionStatus) =>
                    status.severity === 'critical'
            ),
            shareReplay()
        );
    public bannerMessage: string;
    public hasUserTooltipShown = false;
    public feedbackSite: string = this.env.feedbackSite;
    public menuItems: MenuItem[] | undefined;

    public user$ = this.authentication.user$;
    public userMeta$ = this.authentication.userMeta$;
    public isMagayaNetwork$ = this.authentication.isMagayaNetwork$;

    private userSubscription: Subscription;

    @ViewChild('menu') menu: Menu;

    ngOnInit() {
        // Rebuild the user menu whenever the user changes AND whenever the
        // active language finishes changing (onLangChange fires only after
        // the new translations have loaded, unlike `use()` which is async).
        // This prevents the menu from being built with `translate.instant()`
        // before the newly selected language's translations are available.
        this.userSubscription = combineLatest([
            this.user$,
            this.translate.onLangChange.pipe(startWith(null)),
        ]).subscribe(([user]) => {
            this.menuItems = [
                {
                    label: `${this.translate.instant('hub.menu.top-menu.user')}: ${user.email}`,
                },
                {
                    label: `${this.translate.instant('hub.menu.top-menu.organization')}: ${user.organization_id} (${user.organization_name})`,
                },
                {
                    label: `${this.translate.instant('hub.menu.top-menu.role')}: ${user.role_name}`,
                },
                {
                    divider: true,
                },
                {
                    label: this.translate.instant('hub.menu.top-menu.profile'),
                    icon: 'user',
                    command: () => this.router.navigate(['/user', 'profile']),
                },
                {
                    divider: true,
                },
                {
                    label: this.translate.instant('hub.menu.top-menu.log-out'),
                    icon: 'lock',
                    command: () => this.logout(),
                },
            ];
        });
    }

    public logout() {
        this.authentication.logout();
    }

    public openMenu(event: Event): void {
        this.menu.toggle(event);
    }

    public resetImpersonation() {
        this.authentication.resetImpersonation();
    }

    private bannerFactory(status: Hub.MagayaConnectionStatus) {
        const title = this.translate.instant(
            'hub.home.magaya-connection-issue'
        );
        const message = `${
            status.message
        }<br /><br /><small>${this.translate.instant(
            'hub.home.last-checked'
        )} ${this.timeagoPipe.transform(
            status.updated_at
        )}<br />(${this.translate.instant(
            'hub.home.updates-every-15-minutes'
        )})</small>`;

        switch (status.severity) {
            case 'high':
                this.toastr.error(message, title, {
                    enableHtml: true,
                    timeOut: 1e4,
                });
                break;
            case 'critical':
                this.toastr.error(message, title, {
                    enableHtml: true,
                    timeOut: 1e4,
                });
                break;
            case 'low':
            case 'medium':
                this.toastr.warning(message, title, {
                    enableHtml: true,
                    timeOut: 1e4,
                });
                break;
        }
    }

    public ngOnDestroy(): void {
        this.userSubscription.unsubscribe();
    }
}
